import React, { useState } from 'react';
import Card, { CardRow } from 'components/styleguide/card/Card';
import Sheet from 'components/styleguide/sheet_modal/Sheet';
import InputField from 'components/styleguide/input_field/InputField';
import SelectField from 'components/styleguide/select_field/SelectField';
import { useEditableSheet } from 'components/styleguide/sheet_modal/useEditableSheet';
import LocationField from 'components/styleguide/location_field/LocationField';
import Icon from 'components/styleguide/icon/Icon';
import PhoneNumberField from 'components/styleguide/phone_number_field/PhoneNumberField';
const styles = require('../onboarding-cards.module.scss');

interface SelectOption {
  label: string;
  value: string;
}

interface ProfileDetailsCardProps {
  talent: {
    name: string;
    city: string;
    country_code: string;
    country_name: string;
    citizenship: string;
    phoneNumber: string;
    linkedin: string;
  };
  citizenships: string[];
  url: string;
  required?: boolean;
}

const ProfileDetailsCard: React.FC<ProfileDetailsCardProps> = ({
  talent,
  citizenships,
  url,
  required = false,
}) => {
  const citizenshipOptions: SelectOption[] = citizenships.map(c => ({ label: c, value: c }));

  const parseCitizenships = (str: string): string[] => {
    if (!str) return [];
    return str.split(', ');
  };

  const [display, setDisplay] = useState({
    name: talent.name,
    city: talent.city,
    countryCode: talent.country_code,
    countryName: talent.country_name,
    citizenship: talent.citizenship,
    phoneNumber: talent.phoneNumber,
    linkedin: talent.linkedin,
  });

  const [countryName, setCountryName] = useState(talent.country_name);

  const sheet = useEditableSheet({
    url,
    payloadKey: 'talent',
    onSuccess: (formData) => {
      const citizenshipValues = Array.isArray(formData.citizenships) ? formData.citizenships : [];
      setDisplay({
        name: formData.name,
        city: formData.city,
        countryCode: formData.country_code,
        countryName: countryName,
        citizenship: citizenshipValues.join(', '),
        phoneNumber: formData.phone_number,
        linkedin: formData.linkedin,
      });
    },
  });

  const displayMap: Record<string, any> = {
    name: display.name,
    city: display.city,
    country_code: display.countryCode,
    citizenship: display.citizenship,
    phone_number: display.phoneNumber,
    linkedin: display.linkedin,
  };

  const mandatoryFields = ['name', 'city', 'country_code', 'citizenship'];

  const missingIcon = (...fields: string[]) => {
    if (!fields.some(f => !displayMap[f])) return null;

    const isMandatory = required && fields.some(f => mandatoryFields.includes(f) && !displayMap[f]);
    return <Icon name={isMandatory ? 'triangleAlert' : 'circleAlert'} size="xs" color={isMandatory ? 'error-dark' : 'warning-dark'} />;
  };

  const hasAll = !!(display.name && display.city && display.citizenship && display.phoneNumber && display.linkedin);
  const hasMandatoryMissing = required && mandatoryFields.some(f => !displayMap[f]);
  const tag = hasAll
    ? undefined
    : hasMandatoryMissing
      ? { variant: 'error' as const, icon: 'triangleAlert' as const, children: 'Mandatory' }
      : { variant: 'warning' as const, icon: 'circleAlert' as const, children: 'Missing' };

  const handleEditClick = () => {
    sheet.open('Edit profile details', {
      name: display.name,
      city: display.city,
      country_code: display.countryCode,
      citizenships: parseCitizenships(display.citizenship),
      phone_number: display.phoneNumber,
      linkedin: display.linkedin,
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    sheet.handleFormChange(e.target.name, e.target.value);
  };

  const handleLocationChange = (e: any) => {
    const addressArray = e.formatted_address.split(', ');
    const city = addressArray[0];
    const countryComponent = e.address_components.find(
      (component: any) => component.types.includes('country')
    );
    const countryShort = countryComponent?.short_name || '';
    const countryLong = countryComponent?.long_name || '';

    sheet.handleFormChange('city', city);
    sheet.handleFormChange('country_code', countryShort);
    setCountryName(countryLong);
  };

  const handleCitizenshipChange = (selected: SelectOption[]) => {
    sheet.handleFormChange('citizenships', (selected || []).map(s => s.value));
  };

  const handlePhoneChange = (value: string) => {
    sheet.handleFormChange('phone_number', value);
  };

  return (
    <>
      <Card
        title="Basic Information"
        iconName="file"
        context="talent"
        tag={tag}
        onEdit={handleEditClick}
      >
        <CardRow context="talent">
          <div className={styles.fieldGrid}>
            <div className={styles.field}>
              <p className={styles.label}>
                Full name
                {missingIcon('name')}
              </p>
              <p className={styles.value}>{display.name || '-'}</p>
            </div>
            <div className={styles.field}>
              <p className={styles.label}>
                Location
                {missingIcon('city', 'country_code')}
              </p>
              <p className={styles.value}>{[display.city, display.countryName].filter(Boolean).join(', ') || '-'}</p>
            </div>
            <div className={styles.field}>
              <p className={styles.label}>
                Citizenship
                {missingIcon('citizenship')}
              </p>
              <p className={styles.value}>{display.citizenship || '-'}</p>
            </div>
            <div className={styles.field}>
              <p className={styles.label}>
                Phone number
                {missingIcon('phone_number')}
              </p>
              <p className={styles.value}>{display.phoneNumber || '-'}</p>
            </div>
            <div className={styles.field}>
              <p className={styles.label}>
                LinkedIn
                {missingIcon('linkedin')}
              </p>
              <p className={styles.value}>{display.linkedin || '-'}</p>
            </div>
          </div>
        </CardRow>
      </Card>

      {sheet.isOpen && (
        <Sheet
          title={sheet.sheetTitle}
          onClose={sheet.close}
          context="talent"
          showCancelButton={false}
          continueButtonText="Save"
          onContinue={sheet.handleSave}
        >
          <div className={styles.form}>
            <InputField
              label="Full name"
              placeholder="e.g. John Doe"
              name="name"
              required={true}
              value={sheet.formData.name || ''}
              onChange={handleInputChange}
              size="L"
              error={sheet.getError('name')}
            />
            <LocationField
              error={sheet.getError('city') || sheet.getError('country_code')}
              onLocationChange={handleLocationChange}
              defaultValue={{"city": sheet.formData.city, "countryCode": sheet.formData.country_code, "country": countryName}}
              value={{"city": sheet.formData.city, "countryCode": sheet.formData.country_code, "country": countryName}}
              label="Location"
              required={true}
            />
            <SelectField
              label="Citizenship"
              name="citizenships"
              placeholder="e.g. Portuguese"
              options={citizenshipOptions}
              isMulti={true}
              value={citizenshipOptions.filter(o => (sheet.formData.citizenships || []).includes(o.value))}
              onChange={handleCitizenshipChange}
              size="L"
              userType="talent"
              error={sheet.getError('citizenships')}
              required={true}
            />
            <PhoneNumberField
              label="Phone number"
              value={sheet.formData.phone_number || ''}
              onChange={handlePhoneChange}
              error={sheet.getError('phone_number')}
            />
            <InputField
              label="LinkedIn"
              placeholder="e.g. https://linkedin.com/in/johndoe"
              name="linkedin"
              value={sheet.formData.linkedin || ''}
              onChange={handleInputChange}
              size="L"
              error={sheet.getError('linkedin')}
            />
          </div>
        </Sheet>
      )}
    </>
  );
};

export default ProfileDetailsCard;
